/**
 * core/api/mutations.ts — All monday.com GraphQL write operations.
 *
 * Each function accepts strongly-typed arguments, builds the appropriate
 * GraphQL mutation, and returns the created/updated item from the API.
 */

import { gqlClient } from './client';
import type { MondayItem, Priority } from '../types';

// ─── Create Item (generic) ───────────────────────────────────────────────────

const CREATE_ITEM = `
  mutation CreateItem(
    $boardId: ID!
    $groupId: String
    $itemName: String!
    $columnValues: JSON
  ) {
    create_item(
      board_id: $boardId
      group_id: $groupId
      item_name: $itemName
      column_values: $columnValues
    ) {
      id
      name
      board { id name }
      created_at
    }
  }
`;

export interface CreateItemInput {
  boardId: string;
  groupId?: string;
  itemName: string;
  columnValues?: Record<string, unknown>;
}

export async function mutateCreateItem(
  input: CreateItemInput,
): Promise<MondayItem> {
  const data = await gqlClient<{ create_item: MondayItem }>(CREATE_ITEM, {
    boardId: input.boardId,
    groupId: input.groupId ?? null,
    itemName: input.itemName,
    columnValues: input.columnValues
      ? JSON.stringify(input.columnValues)
      : null,
  });
  return data.create_item;
}

// ─── Create Note ─────────────────────────────────────────────────────────────

export interface CreateNoteInput {
  boardId: string;
  title: string;
  content?: string;
  type?: string;
  topicId?: string;
  tags?: string[];
}

/**
 * Creates a note item on the Notes board.
 * Column IDs must match the Notes board schema from DATA_MODEL.md.
 */
export async function mutateCreateNote(
  input: CreateNoteInput,
): Promise<MondayItem> {
  const columnValues: Record<string, unknown> = {};

  if (input.content) columnValues['long_text'] = { text: input.content };
  if (input.type) columnValues['status'] = { label: input.type };
  if (input.topicId) columnValues['connect_boards'] = { linkedPulseIds: [{ linkedPulseId: Number(input.topicId) }] };
  if (input.tags && input.tags.length > 0) {
    columnValues['tags'] = { tag_ids: [] }; // Tag IDs must be resolved separately
  }

  return mutateCreateItem({
    boardId: input.boardId,
    itemName: input.title,
    columnValues,
  });
}

// ─── Create Action ────────────────────────────────────────────────────────────

export interface CreateActionInput {
  boardId: string;
  title: string;
  priority?: Priority;
  dueDate?: string; // YYYY-MM-DD
  description?: string;
  tags?: string[];
}

/**
 * Creates an action item on the Actions board.
 * Column IDs must match the Actions board schema from DATA_MODEL.md.
 */
export async function mutateCreateAction(
  input: CreateActionInput,
): Promise<MondayItem> {
  const columnValues: Record<string, unknown> = {};

  if (input.priority) columnValues['status'] = { label: input.priority };
  if (input.dueDate) columnValues['date4'] = { date: input.dueDate };
  if (input.description) columnValues['long_text'] = { text: input.description };

  return mutateCreateItem({
    boardId: input.boardId,
    itemName: input.title,
    columnValues,
  });
}

// ─── Create Meeting ───────────────────────────────────────────────────────────

export interface CreateMeetingInput {
  boardId: string;
  title: string;
  date?: string; // YYYY-MM-DD
  attendees?: string[];
  notes?: string;
}

export async function mutateCreateMeeting(
  input: CreateMeetingInput,
): Promise<MondayItem> {
  const columnValues: Record<string, unknown> = {};

  if (input.date) columnValues['date4'] = { date: input.date };
  if (input.notes) columnValues['long_text'] = { text: input.notes };
  if (input.attendees && input.attendees.length > 0) {
    columnValues['text'] = input.attendees.join(', ');
  }

  return mutateCreateItem({
    boardId: input.boardId,
    itemName: input.title,
    columnValues,
  });
}

// ─── Escalate Action ─────────────────────────────────────────────────────────

export interface EscalateActionInput {
  targetBoardId: string;
  itemName: string;
  description?: string;
  priority?: Priority;
  /** ID of the source action item, recorded in a text column */
  sourceItemId?: string;
}

/**
 * Creates a new item on the target board to represent an escalated action.
 */
export async function mutateEscalateAction(
  input: EscalateActionInput,
): Promise<MondayItem> {
  const columnValues: Record<string, unknown> = {};

  if (input.description) columnValues['long_text'] = { text: input.description };
  if (input.priority) columnValues['status'] = { label: input.priority };
  if (input.sourceItemId) columnValues['text'] = `Escalated from Vault item #${input.sourceItemId}`;

  return mutateCreateItem({
    boardId: input.targetBoardId,
    itemName: input.itemName,
    columnValues,
  });
}

// ─── Change Column Values ─────────────────────────────────────────────────────

const CHANGE_COLUMN_VALUES = `
  mutation ChangeColumnValues(
    $boardId: ID!
    $itemId: ID!
    $columnValues: JSON!
  ) {
    change_multiple_column_values(
      board_id: $boardId
      item_id: $itemId
      column_values: $columnValues
    ) {
      id
      name
      updated_at
    }
  }
`;

export interface ChangeColumnValuesInput {
  boardId: string;
  itemId: string;
  columnValues: Record<string, unknown>;
}

export async function mutateChangeColumnValues(
  input: ChangeColumnValuesInput,
): Promise<MondayItem> {
  const data = await gqlClient<{ change_multiple_column_values: MondayItem }>(
    CHANGE_COLUMN_VALUES,
    {
      boardId: input.boardId,
      itemId: input.itemId,
      columnValues: JSON.stringify(input.columnValues),
    },
  );
  return data.change_multiple_column_values;
}

// ─── Mark Notification Read ───────────────────────────────────────────────────

const MARK_NOTIFICATIONS_READ = `
  mutation MarkNotificationsRead {
    mark_all_notifications_as_read {
      id
    }
  }
`;

export async function mutateMarkAllNotificationsRead(): Promise<void> {
  await gqlClient(MARK_NOTIFICATIONS_READ);
}

// ─── Board Management (installer only) ────────────────────────────────────────
//
// These mutations are used exclusively by the board-provisioning utilities
// (boardSetup.ts + scripts/setupBoards.ts / scripts/setup.ts).  They should
// never be called from React components — use the domain-specific helpers above
// for all runtime writes.

const CREATE_BOARD = `
  mutation CreateBoard($name: String!, $kind: BoardKind!) {
    create_board(board_name: $name, board_kind: $kind) {
      id
      name
    }
  }
`;

export async function mutateCreateBoard(
  name: string,
  kind: 'share' | 'public' | 'private' = 'share',
): Promise<{ id: string; name: string }> {
  const data = await gqlClient<{ create_board: { id: string; name: string } }>(
    CREATE_BOARD,
    { name, kind },
  );
  return data.create_board;
}

const CREATE_COLUMN = `
  mutation CreateColumn(
    $boardId: ID!
    $title: String!
    $columnType: ColumnType!
    $defaults: JSON
  ) {
    create_column(
      board_id: $boardId
      title: $title
      column_type: $columnType
      defaults: $defaults
    ) {
      id
      title
      type
    }
  }
`;

export async function mutateCreateColumn(
  boardId: string,
  title: string,
  columnType: string,
  settings?: Record<string, unknown>,
): Promise<{ id: string }> {
  const data = await gqlClient<{ create_column: { id: string } }>(CREATE_COLUMN, {
    boardId,
    title,
    columnType,
    defaults: settings ? JSON.stringify(settings) : null,
  });
  return data.create_column;
}

const CREATE_GROUP = `
  mutation CreateGroup($boardId: ID!, $groupName: String!) {
    create_group(board_id: $boardId, group_name: $groupName) {
      id
    }
  }
`;

export async function mutateCreateGroup(
  boardId: string,
  groupName: string,
): Promise<{ id: string }> {
  const data = await gqlClient<{ create_group: { id: string } }>(CREATE_GROUP, {
    boardId,
    groupName,
  });
  return data.create_group;
}

const DELETE_BOARD = `
  mutation DeleteBoard($boardId: ID!) {
    delete_board(board_id: $boardId) {
      id
    }
  }
`;

export async function mutateDeleteBoard(boardId: string): Promise<void> {
  await gqlClient(DELETE_BOARD, { boardId });
}

// ── Additional mutations ────────────────────────────────────────────────────

export interface UpdateActionInput {
  boardId: string;
  itemId: string;
  status?: string;
  priority?: string;
  dueDate?: string;
  description?: string;
}

export async function mutateUpdateAction(input: UpdateActionInput): Promise<MondayItem> {
  const cv: Record<string, unknown> = {};
  if (input.status)      cv['status']    = { label: input.status };
  if (input.priority)    cv['priority']  = { label: input.priority };
  if (input.dueDate)     cv['date4']     = { date: input.dueDate };
  if (input.description) cv['long_text'] = { text: input.description };
  return mutateChangeColumnValues({ boardId: input.boardId, itemId: input.itemId, columnValues: cv });
}

export interface CreateTopicInput {
  boardId: string;
  name: string;
  color?: string;
  description?: string;
}

export async function mutateCreateTopic(input: CreateTopicInput): Promise<MondayItem> {
  const cv: Record<string, unknown> = {};
  if (input.color)       cv['color_picker'] = input.color;
  if (input.description) cv['long_text']    = { text: input.description };
  return mutateCreateItem({ boardId: input.boardId, itemName: input.name, columnValues: cv });
}

export interface UpdateNoteInput {
  boardId: string;
  itemId: string;
  title?: string;
  content?: string;
  type?: string;
}

export async function mutateUpdateNote(input: UpdateNoteInput): Promise<MondayItem> {
  const cv: Record<string, unknown> = {};
  if (input.content) cv['long_text'] = { text: input.content };
  if (input.type)    cv['status']    = { label: input.type };
  return mutateChangeColumnValues({ boardId: input.boardId, itemId: input.itemId, columnValues: cv });
}
